import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getCheckoutStatus } from '../paymentsApi';

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 6;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');

  const [state, setState] = useState('polling'); // polling | paid | expired | error | timeout
  const [details, setDetails] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      return;
    }

    let cancelled = false;
    let timer = null;

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState('timeout');
        return;
      }
      attemptsRef.current += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        setDetails(data);
        if (data.payment_status === 'paid') {
          setState('paid');
          return;
        }
        if (data.status === 'expired') {
          setState('expired');
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  const renderIcon = () => {
    if (state === 'paid') return <CheckCircle2 className="h-14 w-14 text-emerald-600" strokeWidth={1.5} />;
    if (state === 'expired' || state === 'error') return <XCircle className="h-14 w-14 text-red-500" strokeWidth={1.5} />;
    if (state === 'timeout') return <Clock className="h-14 w-14 text-amber-500" strokeWidth={1.5} />;
    return <Loader2 className="h-14 w-14 animate-spin text-neutral-500" strokeWidth={1.5} />;
  };

  const renderTitle = () => {
    switch (state) {
      case 'paid': return 'Payment successful';
      case 'expired': return 'Checkout expired';
      case 'error': return 'Something went wrong';
      case 'timeout': return 'Still processing…';
      default: return 'Verifying your payment…';
    }
  };

  const renderBody = () => {
    switch (state) {
      case 'paid':
        return (
          <>
            <p className="text-neutral-600">
              Thanks for your purchase. Credits and access have been added to your account.
            </p>
            {details?.amount_total != null && (
              <p className="mt-2 text-sm text-neutral-500">
                Charged: {(details.amount_total / 100).toFixed(2)} {details.currency?.toUpperCase()}
              </p>
            )}
          </>
        );
      case 'expired':
        return <p className="text-neutral-600">Your checkout session expired. Please try again.</p>;
      case 'timeout':
        return (
          <p className="text-neutral-600">
            Your payment is taking a bit longer than usual. You'll receive a confirmation email once it clears.
          </p>
        );
      case 'error':
        return <p className="text-neutral-600">We couldn't find that checkout session.</p>;
      default:
        return <p className="text-neutral-600">Please wait while we confirm the payment with Stripe.</p>;
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-6">
      <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-sm">
        <div className="mb-5 flex justify-center">{renderIcon()}</div>
        <h1 className="font-serif text-3xl tracking-tight text-neutral-900">{renderTitle()}</h1>
        <div className="mt-3">{renderBody()}</div>

        <div className="mt-8 flex flex-col gap-2">
          <Button onClick={() => navigate('/')} className="w-full bg-neutral-900 text-white hover:bg-neutral-800">
            Back to AMAIA <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
          {(state === 'expired' || state === 'error') && (
            <Button variant="outline" onClick={() => navigate('/pricing')} className="w-full">
              Try again
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
